import { documentKeys } from '@/hooks/use-documents'
import { useSSEEvent } from '@/hooks/use-event-stream'
import { documentService } from '@/services/document.service'
import type { DocumentStatus } from '@/types'
import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useRef, useState } from 'react'

const MAX_FILE_SIZE = 5 * 1024 * 1024 // 5MB
const ACCEPTED_TYPES = ['application/pdf']

export interface UploadedDocument {
  id: number
  filename: string
  status: DocumentStatus
  error?: string | undefined
}

export interface UseDocumentUploadReturn {
  document: UploadedDocument | null
  isUploading: boolean
  uploadProgress: number // 0-100 for upload, -1 for processing
  error: string | null
  uploadFile: (file: File) => Promise<UploadedDocument | null>
  clearDocument: () => void
  cancelUpload: () => Promise<void>
  setExistingDocument: (doc: UploadedDocument) => void
}

function validateFile(file: File): string | null {
  if (file.size > MAX_FILE_SIZE) {
    return 'File must be under 5MB'
  }
  if (!ACCEPTED_TYPES.includes(file.type)) {
    return 'Only PDF files are supported'
  }
  return null
}

export function useDocumentUpload(): UseDocumentUploadReturn {
  const queryClient = useQueryClient()
  const [document, setDocument] = useState<UploadedDocument | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [error, setError] = useState<string | null>(null)

  // Refs for cancellation and timeout
  const abortControllerRef = useRef<AbortController | null>(null)
  const currentDocumentIdRef = useRef<number | null>(null)
  const isCancelledRef = useRef(false)
  const processingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Listen for SSE events to update document status (replaces polling)
  useSSEEvent('document_status_changed', (event) => {
    const data = event.data as { document_id: number; status: string; error_message?: string }
    const docId = currentDocumentIdRef.current
    if (!docId || data.document_id !== docId) return
    if (isCancelledRef.current) return

    if (data.status === 'ready' || data.status === 'failed' || data.status === 'invalid_document') {
      if (processingTimeoutRef.current) {
        clearTimeout(processingTimeoutRef.current)
        processingTimeoutRef.current = null
      }
      const isError = data.status === 'failed' || data.status === 'invalid_document'
      setDocument((prev) =>
        prev
          ? {
              ...prev,
              status: data.status as DocumentStatus,
              error: isError ? (data.error_message ?? 'Processing failed') : undefined,
            }
          : null,
      )
      setUploadProgress(0)
      if (data.status === 'ready') {
        queryClient.invalidateQueries({ queryKey: documentKeys.list() })
      }
    }
  })

  const cancelUpload = useCallback(async () => {
    isCancelledRef.current = true

    if (processingTimeoutRef.current) {
      clearTimeout(processingTimeoutRef.current)
      processingTimeoutRef.current = null
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      abortControllerRef.current = null
    }

    const documentId = currentDocumentIdRef.current
    if (documentId) {
      try {
        await documentService.deleteDocument(documentId)
      } catch (err) {
        console.warn('Failed to delete document during cancel:', err)
      }
      currentDocumentIdRef.current = null
    }

    setDocument(null)
    setError(null)
    setIsUploading(false)
    setUploadProgress(0)
  }, [])

  const uploadFile = useCallback(
    async (file: File): Promise<UploadedDocument | null> => {
      const validationError = validateFile(file)
      if (validationError) {
        setError(validationError)
        return null
      }

      isCancelledRef.current = false
      abortControllerRef.current = new AbortController()

      setIsUploading(true)
      setUploadProgress(0)
      setError(null)

      try {
        // 1. Get presigned URL
        const { document_id, upload_url } = await documentService.getUploadUrl({
          filename: file.name,
          content_type: file.type,
          file_size: file.size,
        })

        currentDocumentIdRef.current = document_id

        if (isCancelledRef.current) {
          await documentService.deleteDocument(document_id)
          return null
        }

        setDocument({
          id: document_id,
          filename: file.name,
          status: 'uploading',
        })

        queryClient.invalidateQueries({ queryKey: documentKeys.list() })

        // 2. Upload to S3
        await documentService.uploadToS3(
          upload_url,
          file,
          (progress) => setUploadProgress(progress),
          abortControllerRef.current.signal,
        )

        if (isCancelledRef.current) {
          await documentService.deleteDocument(document_id)
          return null
        }

        setUploadProgress(-1)
        setDocument((prev) => (prev ? { ...prev, status: 'processing' } : null))

        // 3. Start processing
        await documentService.startProcessing(document_id)

        // SSE will notify us when processing completes - no polling needed
        const processingDoc: UploadedDocument = {
          id: document_id,
          filename: file.name,
          status: 'processing',
        }
        setDocument(processingDoc)

        // Safety timeout: if processing takes longer than 2 minutes, show error
        processingTimeoutRef.current = setTimeout(() => {
          setDocument((prev) =>
            prev && prev.id === document_id && prev.status === 'processing'
              ? { ...prev, status: 'failed', error: 'Processing timed out. Please try again.' }
              : prev,
          )
          setUploadProgress(0)
        }, 120_000)

        return processingDoc
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          return null
        }

        const errorMessage = err instanceof Error ? err.message : 'Upload failed'
        setError(errorMessage)
        setDocument((prev) => (prev ? { ...prev, status: 'failed', error: errorMessage } : null))
        return null
      } finally {
        setIsUploading(false)
        abortControllerRef.current = null
      }
    },
    [queryClient],
  )

  const clearDocument = useCallback(() => {
    if (isUploading) {
      cancelUpload()
    } else {
      isCancelledRef.current = true

      const documentId = currentDocumentIdRef.current
      if (documentId) {
        documentService.deleteDocument(documentId).catch(() => {
          // Ignore - document may not exist or already deleted
        })
      }

      setDocument(null)
      setError(null)
      setUploadProgress(0)
      currentDocumentIdRef.current = null
    }
  }, [isUploading, cancelUpload])

  const setExistingDocument = useCallback((doc: UploadedDocument) => {
    setDocument(doc)
    setError(null)
    setUploadProgress(0)
  }, [])

  return {
    document,
    isUploading,
    uploadProgress,
    error,
    uploadFile,
    clearDocument,
    cancelUpload,
    setExistingDocument,
  }
}
